import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DashboardLayout } from "./Dashboard";
import TableWithSearch from "@/components/TableWithSearch";
import { useParams } from "react-router";
import { billingAnalysisHeaders } from "@/utils/tablesData";
import {
  getErrToast,
  getReportDateRange,
  type ReportDatePreset,
} from "@/utils/helpers";
import AxiosInstance from "@/utils/AxiosInstance";
import { toast } from "sonner";
import type { DateRange } from "react-day-picker";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { CalendarDays } from "lucide-react";
import { isAfter, startOfDay } from "date-fns";

const PRESET_OPTIONS: { value: ReportDatePreset; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last7", label: "Last 7 Days" },
  { value: "last15", label: "Last 15 Days" },
  { value: "last30", label: "Last 30 Days" },
  { value: "last90", label: "Last 90 Days" },
  { value: "custom", label: "Custom date range" },
];

const OrganizationbillingAnalysis = () => {
    const { id } = useParams();
    const [analysis, setAnalysis] = useState([]);
    const [datePreset, setDatePreset] = useState<ReportDatePreset>("last30");
    const [customRange, setCustomRange] = useState<DateRange | undefined>();
    const [draftCustomRange, setDraftCustomRange] = useState<DateRange | undefined>();
    const [customPickerOpen, setCustomPickerOpen] = useState(false);
    const [activeRangeBoundary, setActiveRangeBoundary] = useState<
      "from" | "to" | null
    >(null);

    // Yadi-only UX requirement: show only Month + Custom date range options.
    // Show Month + Custom date range for ALL organizations (no orgId gating).
    const isYadiSpecial = true;

    const [yadiMode, setYadiMode] = useState<"month" | "custom">("month");
    const [selectedMonth, setSelectedMonth] = useState<string>(() =>
      String(new Date().getMonth() + 1).padStart(2, "0"),
    ); // "01".."12"
    const [selectedYear, setSelectedYear] = useState<string>(() =>
      String(new Date().getFullYear()),
    );
    const selectedMonthKey = useMemo(
      () => `${selectedYear}-${selectedMonth}`, // YYYY-MM
      [selectedYear, selectedMonth],
    );

    const monthOptions = useMemo(() => {
      const fmt = new Intl.DateTimeFormat(undefined, { month: "long" });
      return Array.from({ length: 12 }, (_, i) => {
        const m = String(i + 1).padStart(2, "0");
        const label = fmt.format(new Date(2020, i, 1)); // stable year
        return { key: m, label };
      });
    }, []);

    const yearOptions = useMemo(() => {
      // Show a reasonable window (current year and last 9 years)
      const nowY = new Date().getFullYear();
      const out: { key: string; label: string }[] = [];
      for (let y = nowY; y >= nowY - 9; y--) {
        out.push({ key: String(y), label: String(y) });
      }
      return out;
    }, []);

    const { startDate, endDate } = useMemo(() => {
      if (!isYadiSpecial) {
        return getReportDateRange(
          datePreset,
          customRange?.from,
          customRange?.to ?? customRange?.from,
        );
      }

      if (yadiMode === "custom") {
        return getReportDateRange(
          "custom",
          customRange?.from,
          customRange?.to ?? customRange?.from,
        );
      }

      // month mode
      const [yStr, mStr] = String(selectedMonthKey).split("-");
      const y = Number(yStr);
      const m = Number(mStr);
      if (!y || !m) return getReportDateRange("last30");
      const from = new Date(y, m - 1, 1);
      const to = new Date(y, m, 0); // last day of month
      return getReportDateRange("custom", from, to);
    }, [isYadiSpecial, datePreset, customRange, yadiMode, selectedMonthKey]);

    const [filters, setFilters] = useState<Record<string, any>>({
      startDate,
      endDate,
    });

    useEffect(() => {
      setFilters((prev) => ({
        ...prev,
        startDate,
        endDate,
      }));
    }, [startDate, endDate]);

    const isFutureCalendarDay = (d: Date) =>
      isAfter(startOfDay(d), startOfDay(new Date()));

    const handleCustomRangeSelect = (_next: DateRange | undefined, day?: Date) => {
      if (!day || isFutureCalendarDay(day)) return;
      setDraftCustomRange((prev) => {
        const from = prev?.from;
        const to = prev?.to;

        if (!from || !to) {
          if (from && !to) {
            setActiveRangeBoundary(null);
            return day < from ? { from: day, to: from } : { from, to: day };
          }
          setActiveRangeBoundary("to");
          return { from: day, to: undefined };
        }

        if (day.toDateString() === from.toDateString()) {
          setActiveRangeBoundary("from");
          return prev;
        }
        if (day.toDateString() === to.toDateString()) {
          setActiveRangeBoundary("to");
          return prev;
        }

        if (activeRangeBoundary === "from") {
          setActiveRangeBoundary(null);
          return day <= to ? { from: day, to } : { from: to, to: day };
        }
        if (activeRangeBoundary === "to") {
          setActiveRangeBoundary(null);
          return day >= from ? { from, to: day } : { from: day, to: from };
        }

        setActiveRangeBoundary("to");
        return { from: day, to: undefined };
      });
    };

    const resetDateFilters = () => {
      setDatePreset("last30");
      setCustomRange(undefined);
      setDraftCustomRange(undefined);
      setCustomPickerOpen(false);
      setActiveRangeBoundary(null);
    };

    const handleCustomPickerOpenChange = (open: boolean) => {
      setCustomPickerOpen(open);
      if (open) {
        setDraftCustomRange(customRange);
        setActiveRangeBoundary(null);
        return;
      }
      setCustomRange(draftCustomRange);
      setActiveRangeBoundary(null);
    };

    /** Filter dropdown "clear all" replaces entire `filters`; preserve date range params. */
    const handleClearAllFilters = useCallback(() => {
      setFilters({
        patientType: "",
        startDate,
        endDate,
      });
    }, [startDate, endDate]);

    const dateRangeToolbar = (
      <div className="flex flex-nowrap items-center gap-2 shrink-0">
        {isYadiSpecial ? (
          <div className="flex items-center gap-1.5 h-9 rounded-lg border border-gray-300 bg-white px-1.5 shrink-0">
            <Select
              value={yadiMode}
              onValueChange={(v) => {
                const mode = v as "month" | "custom";
                setYadiMode(mode);
                if (mode === "custom") {
                  handleCustomPickerOpenChange(true);
                } else {
                  setCustomPickerOpen(false);
                  setActiveRangeBoundary(null);
                }
              }}
            >
              <SelectTrigger className="h-8 border-0 shadow-none px-1.5 w-[min(130px,32vw)]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="month">Month</SelectItem>
                <SelectItem value="custom">Custom date range</SelectItem>
              </SelectContent>
            </Select>

            {yadiMode === "month" ? (
              <>
                <div className="h-5 w-px bg-gray-200 mx-0.5" />
                <Select
                  value={selectedMonth}
                  onValueChange={(v) => setSelectedMonth(String(v))}
                >
                  <SelectTrigger className="h-8 border-0 shadow-none px-1.5 w-[min(120px,30vw)]">
                    <SelectValue placeholder="Month" />
                  </SelectTrigger>
                  <SelectContent>
                    {monthOptions.map((o) => (
                      <SelectItem key={o.key} value={o.key}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={selectedYear}
                  onValueChange={(v) => setSelectedYear(String(v))}
                >
                  <SelectTrigger className="h-8 border-0 shadow-none px-1.5 w-[min(96px,24vw)]">
                    <SelectValue placeholder="Year" />
                  </SelectTrigger>
                  <SelectContent>
                    {yearOptions.map((o) => (
                      <SelectItem key={o.key} value={o.key}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            ) : null}

            {yadiMode === "custom" ? (
              <>
                <div className="h-5 w-px bg-gray-200 mx-0.5" />
                <Popover
                  open={customPickerOpen}
                  onOpenChange={handleCustomPickerOpenChange}
                >
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      className="h-8 px-1.5 rounded-md whitespace-nowrap text-foreground hover:bg-gray-50 hover:text-foreground"
                    >
                      <CalendarDays className="mr-2 h-4 w-4" />
                      {draftCustomRange?.from && draftCustomRange?.to
                        ? `${draftCustomRange.from.toLocaleDateString()} — ${draftCustomRange.to.toLocaleDateString()}`
                        : draftCustomRange?.from
                          ? `${draftCustomRange.from.toLocaleDateString()} — …`
                          : "Pick dates"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="range"
                      numberOfMonths={2}
                      selected={draftCustomRange}
                      onSelect={handleCustomRangeSelect as any}
                      defaultMonth={draftCustomRange?.from}
                      disabled={(date) => isFutureCalendarDay(date)}
                    />
                  </PopoverContent>
                </Popover>
              </>
            ) : null}
          </div>
        ) : (
          <Select
            value={datePreset}
            onValueChange={(v) => {
              const preset = v as ReportDatePreset;
              setDatePreset(preset);
              if (preset !== "custom") {
                setCustomPickerOpen(false);
                setActiveRangeBoundary(null);
              } else {
                handleCustomPickerOpenChange(true);
              }
            }}
          >
            <SelectTrigger className="w-[min(200px,42vw)] h-9 shrink-0 rounded-lg border-gray-300">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PRESET_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {!isYadiSpecial && datePreset === "custom" && (
          <Popover
            open={customPickerOpen}
            onOpenChange={handleCustomPickerOpenChange}
          >
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="h-9 shrink-0 rounded-lg whitespace-nowrap"
              >
                <CalendarDays className="mr-2 h-4 w-4" />
                {draftCustomRange?.from && draftCustomRange?.to
                  ? `${draftCustomRange.from.toLocaleDateString()} — ${draftCustomRange.to.toLocaleDateString()}`
                  : draftCustomRange?.from
                    ? `${draftCustomRange.from.toLocaleDateString()} — …`
                    : "Pick dates"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="range"
                numberOfMonths={2}
                selected={draftCustomRange}
                onSelect={handleCustomRangeSelect as any}
                defaultMonth={draftCustomRange?.from}
                disabled={(date) => isFutureCalendarDay(date)}
              />
            </PopoverContent>
          </Popover>
        )}

        {/* <Button
          type="button"
          variant="default"
          size="sm"
          className="h-9 shrink-0 rounded-lg"
          onClick={resetDateFilters}
        >
          Reset
        </Button> */}
      </div>
    );
    const [isExporting, setIsExporting] = useState(false);

    const handleExportDownload = async (ctx?: { visibleColumnIds?: string[] , sort?: { sortKey?: string, sortType?: string } | null }) => {


      if (!id) return;
      setIsExporting(true);
      const t = toast.loading('Preparing export...');
      try {
        const api = AxiosInstance();
        const { data } = await api.get<Blob>('/patients/export', {
          params: { 
            orgId: id, 
            ...filters,
            // send visible columns so backend can shape export response
            visibleColumns: Array.isArray(ctx?.visibleColumnIds)
              ? ctx?.visibleColumnIds.join(",")
              : undefined,
            ...(ctx?.sort ? { sortKey: ctx?.sort?.sortKey ?? "", sortType: ctx?.sort?.sortType ?? "" } : {}),
          },
          responseType: 'blob',
        });
        const url = window.URL.createObjectURL(new Blob([data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = 'patients_export.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success('Export downloaded successfully', { id: t });
      } catch (err: any) {
        toast.dismiss(t);
        getErrToast(err);
      } finally {
        setIsExporting(false);
      }
    };
    const filterOptions = [
      {
        label: "Patient Type",
        key: "patientType",
        isMulti: false,
        
        values: [
          { label: "Outpatient", key: "outpatient" },
          { label: "Facility", key: "facility" },
        ],
      },
      // {
      //   label: "Status",
      //   key: "status",
      //   isMulti: false,
      //   values: [
      //     { label: "True", key: "true" },
      //     { label: "False", key: "false" },
      //   ],
      // },
    ]
  return (
<DashboardLayout activePage='/billing-analysis'>
<TableWithSearch
url={'/patients/billing-analysis'}
apiDataKey='patients'
columnPreferenceKey={`org-billing-analysis-${id}`}
columnHeaders={billingAnalysisHeaders}
data={analysis}
setData={setAnalysis}
 viewData={analysis.map((v: any) => {
            const toDateOnly = (val: any) =>
              val ? new Date(val).toISOString().split("T")[0] : val ?? "";
            return {
            ...v,
            dateOfBirth: toDateOnly(v?.dateOfBirth),
            createdAt: toDateOnly(v?.createdAt),
            updatedAt: toDateOnly(v?.updatedAt),
            firstName: `${v?.firstName || ""} ${v?.middleName || ""} ${v?.lastName || ""}`,
            address:
              v?.typeOfPatient === "outpatient"
                ? String(v?.address ?? "").trim()
                : String(v?.organizationId?.address ?? "").trim(),
            organizationId: v?.organizationId?.organizationName,
            assignedProviderId:
              v?.assignedProviderId?.firstName ||
              "" + " " + v?.assignedProviderId?.middleName ||
              "" + " " + v?.assignedProviderId?.lastName ||
              "",
            assignedCaretakerIds: v?.assignedCaretakerIds
              ?.map(
                (c: any) =>
                  c?.firstName ||
                  "" + " " + c?.middleName ||
                  "" + " " + c?.lastName ||
                  "",
              )
              .join(", "),
            facilityName: v?.facilityId?.name || "-",
            heightValue: v?.heightValue?.includes("_")
              ? `${v.heightValue.split("_")[0]}' ${v.heightValue.split("_")[1]}"`
              : v?.heightValue,
            hasCCMCarePlan: v?.hasCCMCarePlan === "yes" ? "Yes" : v?.hasCCMCarePlan === "no" ? "No" : v?.hasCCMCarePlan || "-",
            hasPCMCarePlan: v?.hasPCMCarePlan === "yes" ? "Yes" : v?.hasPCMCarePlan === "no" ? "No" : v?.hasPCMCarePlan || "-",
            hasRPMCarePlan: v?.hasRPMCarePlan === "yes" ? "Yes" : v?.hasRPMCarePlan === "no" ? "No" : v?.hasRPMCarePlan || "-",
              ...(v?.caretakerMinutes ? v?.caretakerMinutes : {} )
            };
          })}
fetchCondition={true}
filters={filters}
setFilters={setFilters}
filtersOptions={filterOptions}
heading='Billing Analysis'
headerExtraControls={dateRangeToolbar}
searchWrapperClassName="w-full max-w-[300px] shrink-0 md:!w-[300px] md:!max-w-[300px] md:!basis-[300px]"
onClearAllFilters={handleClearAllFilters}
isColumnSelection
isDownloadData
downloadAccessModule="canAccessOrganizations"
onDownload={handleExportDownload}
searchPlaceholder='Search patients...'
clickRow={(data)=> {}}
isIncludeSearch
params={{orgId: id}}
isActionColumn={false}
handleAction={()=> {}}

key={'billing-analysis'}


/>
</DashboardLayout>
  )
}

export default OrganizationbillingAnalysis